import { extractEditorDocument, isTextNode } from '../../../NewBlogSystem/utilities/htmlEditorUtils.js';
import { getSerializedByteSize } from '../../../NewBlogSystem/utilities/htmlImageReferenceUtils.js';
import { extractPortfolioReferences } from '../../../NewBlogSystem/utilities/Portfolios/publishedAssetUtils.js';
import { collectGoogleFontsFromClasses, getPortfolioFontTheme } from '../../../NewBlogSystem/utilities/googleFonts.js';
import { assertCompleteChecklistDescriptions } from '../../utilities/scoreValidation.js';
import portfolioScoringChecklist from '../config/portfolioScoringChecklist.json';

const MAX_ELEMENTS = 120;
const MAX_TEXT_LENGTH = 500;
const MAX_TOTAL_TEXT_LENGTH = 12000;
const MAX_CLASSES_PER_ELEMENT = 12;
const MAX_RECORD_TEXT_LENGTH = 650;
const MAX_REQUIREMENTS_PER_GROUP = 12;
const MAX_SCORING_CUSTOM_CLASSES = 16;
const COLLECTION_TAGS = new Set(['projects', 'skills']);
const SCORE_STYLE_PROPERTIES = new Set([
  'color',
  'backgroundColor',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'textAlign',
  'lineHeight',
  'letterSpacing',
  'margin',
  'padding',
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'display',
  'position',
  'gap',
  'border',
  'borderRadius',
  'boxShadow',
  'opacity',
  'overflow',
  'flex',
  'flexDirection',
  'flexWrap',
  'alignItems',
  'justifyContent',
  'gridTemplateColumns',
  'gridTemplateRows',
]);

const compactText = (value, maxLength = MAX_RECORD_TEXT_LENGTH) => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}...` : text;
};

const compactObject = (value) => Object.fromEntries(
  Object.entries(value).filter(([, entry]) => (
    entry !== '' && entry !== undefined && entry !== null && (!Array.isArray(entry) || entry.length > 0)
  ))
);

const firstText = (...values) => {
  const match = values.find((value) => (
    (typeof value === 'string' || typeof value === 'number') && compactText(value)
  ));
  return compactText(match);
};

const compactStringList = (value, limit = MAX_REQUIREMENTS_PER_GROUP) => {
  const entries = Array.isArray(value) ? value : value ? [value] : [];
  const normalized = entries.map((entry) => (
    typeof entry === 'object'
      ? firstText(entry.title, entry.name, entry.label, entry.description, entry.preferredLabel)
      : compactText(entry, 180)
  )).filter(Boolean);

  return [...new Set(normalized)].slice(0, limit);
};

const getDirectText = (node, remainingText) => {
  if (!Array.isArray(node?.children) || remainingText <= 0) return '';

  return compactText(
    node.children
      .filter(isTextNode)
      .map((child) => child.content)
      .join(' '),
    Math.min(MAX_TEXT_LENGTH, remainingText)
  );
};

const buildSemanticOutline = (root) => {
  const elements = [];
  let totalTextLength = 0;
  let omittedElementCount = 0;

  const visit = (node, depth = 0, insideCollection = false) => {
    if (!node || isTextNode(node)) return;

    const attributes = node.attributes || {};
    const classes = Array.isArray(attributes.class) ? attributes.class : [];
    const isCollectionContent = insideCollection
      || COLLECTION_TAGS.has(node.type)
      || Boolean(attributes['data-project-id'] || attributes['data-skill-id']);
    const isIcon = node.type === 'i'
      || classes.some((className) => className === 'bi' || String(className).startsWith('bi-'));
    if (isCollectionContent && isIcon) return;

    if (elements.length >= MAX_ELEMENTS) {
      omittedElementCount += 1;
      return;
    }

    const text = getDirectText(node, MAX_TOTAL_TEXT_LENGTH - totalTextLength);
    totalTextLength += text.length;
    elements.push(compactObject({
      depth,
      tag: compactText(node.type, 30),
      classes: Array.isArray(attributes.class)
        ? [...new Set(attributes.class.map((name) => compactText(name, 80)).filter(Boolean))]
          .slice(0, MAX_CLASSES_PER_ELEMENT)
        : [],
      text,
      href: compactText(attributes.href, 300),
      alt: compactText(attributes.alt, 300),
      title: compactText(attributes.title, 200),
      role: compactText(attributes.role, 80),
      projectId: compactText(attributes['data-project-id'], 160),
      skillId: compactText(
        attributes['data-skill-id'] || attributes['data-project-skill-id'],
        160
      ),
      imageId: compactText(attributes['data-image-id'], 160),
    }));

    if (Array.isArray(node.children)) {
      node.children.forEach((child) => visit(child, depth + 1, isCollectionContent));
    }
  };

  visit(root);
  return { elements, omittedElementCount, totalTextLength };
};

const filterByIds = (records, ids) => {
  const allowedIds = new Set(ids.map(String));
  return (Array.isArray(records) ? records : []).filter((record) => (
    record?.id && allowedIds.has(String(record.id))
  ));
};

const compactProjects = (projects) => projects.slice(0, 10).map((project) => compactObject({
  id: compactText(project?.id, 160),
  title: firstText(project?.title, project?.projectTitle, project?.name),
  overview: firstText(project?.overview, project?.description, project?.summary),
  technologies: compactStringList(project?.technologies || project?.skills || project?.tags),
  skillIds: compactStringList(project?.skillsUsed),
  url: firstText(project?.projectUrl, project?.linkUrl, project?.url),
}));

const compactSkills = (skills) => skills.slice(0, 24).map((skill) => compactObject({
  id: compactText(skill?.id, 160),
  name: firstText(skill?.name, skill?.title),
  description: firstText(skill?.description, skill?.overview),
  rating: Number.isFinite(Number(skill?.rating))
    ? Math.max(0, Math.min(5, Number(skill.rating)))
    : undefined,
}));

const compactTargetJobs = (jobs) => jobs.slice(0, 8).map((job) => compactObject({
  purpose: 'portfolio-optimization-target-not-work-experience',
  id: compactText(job?.id, 160),
  title: firstText(job?.title, job?.jobTitle, job?.role),
  company: firstText(job?.company, job?.employer, job?.organization),
  description: firstText(job?.description, job?.overview, job?.summary),
  category: firstText(job?.category, job?.type),
  essentialSkills: compactStringList(job?.essentialSkillsAndCompetences),
  essentialKnowledge: compactStringList(job?.essentialKnowledge),
  optionalSkills: compactStringList(job?.optionalSkillsAndCompetences),
  optionalKnowledge: compactStringList(job?.optionalKnowledge),
}));

const compactImages = (images) => images.slice(0, 8).map((image) => compactObject({
  id: compactText(image?.id, 160),
  description: firstText(image?.description, image?.alt),
  descriptionStatus: firstText(image?.description, image?.alt) ? 'available' : 'missing',
}));

const compactStyleDeclaration = (styles = {}) => Object.fromEntries(
  Object.entries(styles)
    .filter(([property, value]) => (
      SCORE_STYLE_PROPERTIES.has(property)
      && value !== ''
      && !/data:/i.test(String(value))
    ))
    .slice(0, 20)
    .map(([property, value]) => [property, compactText(value, 100)])
);

const compactStyleDefinition = (definition = {}) => Object.fromEntries(
  ['base', 'md', 'lg']
    .map((breakpoint) => [breakpoint, compactStyleDeclaration(definition[breakpoint])])
    .filter(([, styles]) => Object.keys(styles).length > 0)
);

const compactUsedCustomStyles = (classes = {}, outlineElements = []) => {
  const usedClassNames = new Set(
    outlineElements.flatMap((element) => element.classes || [])
  );
  return Object.fromEntries(
    [...usedClassNames]
      .filter((className) => Object.prototype.hasOwnProperty.call(classes, className))
      .slice(0, MAX_SCORING_CUSTOM_CLASSES)
      .map((className) => [className, compactStyleDefinition(classes[className])])
      .filter(([, definition]) => Object.keys(definition).length > 0)
  );
};

export const TARGET_JOB_PORTFOLIO_SCORE_CONFIG = Object.freeze({
  ...portfolioScoringChecklist,
  scoringRules: Object.freeze((portfolioScoringChecklist.scoringRules || []).map(String)),
  criteria: Object.freeze(portfolioScoringChecklist.criteria.map((criterion) => Object.freeze({
    ...criterion,
    weight: Number(criterion.weight),
  }))),
});

export const TARGET_JOB_PORTFOLIO_SCORE_CHECKLIST =
  TARGET_JOB_PORTFOLIO_SCORE_CONFIG.criteria;

export const normalizePortfolioScoreChecklist = (value) => {
  const entries = Array.isArray(value) ? value : [];
  if (!entries.length) return [];

  const entriesById = new Map(entries
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => [compactText(entry.id, 120), entry]));

  return TARGET_JOB_PORTFOLIO_SCORE_CHECKLIST.map((criterion) => {
    const source = entriesById.get(criterion.id) || {};
    const rawPoints = Number(source.pointsAwarded ?? source.points ?? source.score ?? 0);
    const pointsAwarded = Math.max(
      0,
      Math.min(criterion.weight, Number.isFinite(rawPoints) ? rawPoints : 0)
    );
    const passed = pointsAwarded >= criterion.weight;
    const modelDescription = compactText(source.description, 600);
    const description = modelDescription || (passed
      ? `This portfolio received full points because it meets this criterion: ${criterion.description}`
      : `This portfolio did not receive full points for this criterion: ${criterion.description}`);

    return {
      id: criterion.id,
      category: criterion.category,
      label: criterion.label,
      weight: criterion.weight,
      pointsAwarded,
      passed,
      status: passed ? 'passed' : 'improve',
      description,
      improvement: passed ? '' : criterion.improvement,
    };
  });
};

export const SCORE_RECOMMENDATION_CATEGORIES = Object.freeze([
  'design',
  'wording',
  'images',
  'job-fit',
]);

const normalizeScoreTextList = (value, limit = 8) => (
  Array.isArray(value)
    ? value.map((item) => compactText(item, 700)).filter(Boolean).slice(0, limit)
    : []
);

const normalizeScoreRecommendations = (value) => (
  Array.isArray(value)
    ? value.map((recommendation) => {
      const category = SCORE_RECOMMENDATION_CATEGORIES.includes(recommendation?.category)
        ? recommendation.category
        : 'job-fit';
      const priority = ['high', 'medium', 'low'].includes(recommendation?.priority)
        ? recommendation.priority
        : 'medium';
      return {
        category,
        title: compactText(recommendation?.title, 220),
        details: compactText(recommendation?.details, 700),
        priority,
      };
    }).filter((recommendation) => recommendation.title).slice(0, 6)
    : []
);

export const normalizePortfolioScoreResult = (candidate) => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    throw new Error('The AI assistant returned an invalid portfolio score. Please try again.');
  }

  const rawChecklist = Array.isArray(candidate.checklist) ? candidate.checklist : [];
  assertCompleteChecklistDescriptions(
    rawChecklist,
    TARGET_JOB_PORTFOLIO_SCORE_CHECKLIST
  );

  const checklist = normalizePortfolioScoreChecklist(rawChecklist);
  const maxScore = TARGET_JOB_PORTFOLIO_SCORE_CONFIG.maxScore;
  const score = checklist.reduce((total, criterion) => total + criterion.pointsAwarded, 0);
  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
  const summary = compactText(candidate.summary, 1600);
  if (!summary) {
    throw new Error('The AI assistant returned a portfolio score without an assessment.');
  }

  const isPerfect = score === maxScore;
  return {
    score,
    maxScore,
    percentage,
    summary,
    improvements: normalizeScoreTextList(candidate.improvements),
    recommendations: normalizeScoreRecommendations(candidate.recommendations),
    strengths: normalizeScoreTextList(candidate.strengths),
    checklist,
    isPerfect,
    ratingLabel: isPerfect
      ? 'Perfect'
      : percentage >= 85
        ? 'Strong, with improvements remaining'
        : percentage >= 70
          ? 'Good progress'
          : percentage >= 50
            ? 'Developing'
            : 'Needs improvement',
  };
};

export const createPortfolioScoreMessagePayload = (scoreResult) => {
  if (!scoreResult || typeof scoreResult !== 'object'
    || !Number.isFinite(scoreResult.score)
    || !Number.isFinite(scoreResult.percentage)) {
    throw new Error('A complete portfolio score is required before it can be displayed.');
  }

  return {
    kind: 'score',
    score: scoreResult,
    percentage: scoreResult.percentage,
  };
};

export const buildScoreRecommendationEditPrompt = (recommendation = {}) => {
  const category = SCORE_RECOMMENDATION_CATEGORIES.includes(recommendation.category)
    ? recommendation.category
    : 'job-fit';
  const title = compactText(recommendation.title, 180);
  const details = compactText(recommendation.details || recommendation.reason, 500);

  return [
    'Implement one portfolio scoring recommendation using a compact edit.',
    `Recommendation type: ${category}.`,
    `Change requested: ${title || 'Improve this portfolio.'}`,
    details ? `Reason and guidance: ${details}` : '',
    'Preserve truthful content and unrelated sections. Tailor the result to the selected target jobs without presenting those jobs as work experience.',
  ].filter(Boolean).join('\n');
};

export const buildPortfolioScoringContext = ({
  portfolioData,
  projects = [],
  skills = [],
  jobs = [],
  images = [],
} = {}) => {
  const editorDocument = extractEditorDocument(portfolioData);
  const references = extractPortfolioReferences(editorDocument);
  const referencedProjects = compactProjects(filterByIds(projects, references.referencedProjects));
  const referencedSkills = compactSkills(filterByIds(skills, references.referencedSkills));
  const referencedImages = compactImages(filterByIds(images, references.referencedImages));
  const targetJobs = compactTargetJobs(Array.isArray(jobs) ? jobs : []);
  const outline = buildSemanticOutline(editorDocument.root);
  const fontTheme = getPortfolioFontTheme(editorDocument);
  const usedFonts = collectGoogleFontsFromClasses(editorDocument.classes).map((font) => font.family);
  const missingImageDescriptions = referencedImages
    .filter((image) => image.descriptionStatus === 'missing')
    .map((image) => image.id);

  const payload = {
    format: 'semantic-portfolio-score-v1',
    scoringGoal: 'Assess how convincingly this student portfolio fits the selected target jobs. Target jobs are application goals, not employment history.',
    scoringRules: TARGET_JOB_PORTFOLIO_SCORE_CONFIG.scoringRules,
    portfolioTitle: compactText(portfolioData?.title, 240),
    targetJobs,
    portfolioEvidence: {
      rootTag: compactText(editorDocument.root?.type, 30),
      elements: outline.elements,
      referencedProjects,
      referencedSkills,
      referencedImages,
      imageDescriptionCoverage: {
        total: referencedImages.length,
        described: referencedImages.length - missingImageDescriptions.length,
        missing: missingImageDescriptions.length,
        missingImageIds: missingImageDescriptions,
      },
      customStyleSummary: compactUsedCustomStyles(
        editorDocument.classes,
        outline.elements
      ),
      typography: compactObject({
        bodyFont: fontTheme.body,
        headingFont: fontTheme.headings,
        usedFonts,
      }),
      omittedElementCount: outline.omittedElementCount || undefined,
    },
    requestedRecommendations: {
      purpose: 'Return practical improvements that can be applied through the visual editor.',
      categories: SCORE_RECOMMENDATION_CATEGORIES,
      maximumRecommendations: 6,
      requiredFields: ['category', 'title', 'details', 'priority'],
      rules: [
        'Base every recommendation on the selected target jobs and truthful portfolio evidence.',
        'Include design and wording recommendations when they would improve employer readability or job fit.',
        'Check image descriptions and alternative text; recommend fixes for missing or weak descriptions.',
        'Use priority high, medium, or low. Keep each recommendation focused enough for one compact editor change.',
      ],
    },
  };

  return {
    payload,
    projects: referencedProjects,
    skills: referencedSkills,
    jobs: targetJobs.map((job) => ({ ...job, title: job.title || 'Untitled target job' })),
    images: referencedImages,
    scorerIndex: {
      projects: referencedProjects.map(({ id, title }) => ({ id, title })),
      skills: referencedSkills.map(({ id, name }) => ({ id, name })),
      jobs: targetJobs.map(({ id, title }) => ({ id, title: title || 'Untitled target job' })),
      images: referencedImages.map(({ id, description }) => ({ id, description })),
    },
    references,
    stats: {
      elementCount: outline.elements.length,
      textCharacterCount: outline.totalTextLength,
      targetJobCount: targetJobs.length,
      projectCount: referencedProjects.length,
      skillCount: referencedSkills.length,
      imageCount: referencedImages.length,
      payloadBytes: getSerializedByteSize(payload),
    },
  };
};

const isMalformedJsonResponse = (error) => {
  if (error instanceof SyntaxError) return true;
  const message = String(error?.message || '').toLowerCase();
  return [
    'json',
    'unexpected token',
    'unexpected end',
    'expected property name',
    'unterminated string',
  ].some((fragment) => message.includes(fragment));
};

export const requestPortfolioScoreWithRetry = async ({
  requestScore,
  modelNames = null,
  onRetry,
} = {}) => {
  if (typeof requestScore !== 'function') {
    throw new Error('Portfolio scoring is not available right now.');
  }

  const prioritizedModels = Array.isArray(modelNames)
    ? [...new Set(modelNames.map(String).map((name) => name.trim()).filter(Boolean))]
    : [];
  if (prioritizedModels.length) {
    let lastError;
    for (let index = 0; index < prioritizedModels.length; index += 1) {
      try {
        return await requestScore(prioritizedModels[index]);
      } catch (error) {
        lastError = error;
        const nextModel = prioritizedModels[index + 1];
        if (!nextModel) break;
        onRetry?.(error, {
          failedModel: prioritizedModels[index],
          nextModel,
          attempt: index + 1,
        });
      }
    }
    throw lastError;
  }

  try {
    return await requestScore();
  } catch (error) {
    if (!isMalformedJsonResponse(error)) throw error;
    onRetry?.();
  }

  try {
    return await requestScore();
  } catch (error) {
    if (isMalformedJsonResponse(error)) {
      throw new Error('The AI assistant returned an incomplete score twice. Please try scoring again.');
    }
    throw error;
  }
};
